import React from "react";
import "../styles/process-visualization.css";
import "../styles/shared-ui.css";
import "../styles/user-stories.css";
import {
  streamUserStories,
  userStoriesErrorMessage,
} from "../services/userStoriesService";

const TYPE_LABELS = {
  TASK: "Task",
  DECISION: "Decision",
  SUB_PROCESS: "Sub-process",
};

// Status line text for each `status` phase of the stream.
const PHASE_MESSAGES = {
  thinking: "AI is analysing the process…",
  writing: "AI is writing the user stories…",
  retrying: "Improving the answer…",
};

// The AI's thinking can run long; keep only the tail on screen.
const MAX_THINKING_CHARS = 20000;

const asList = (value) => (Array.isArray(value) ? value.filter(Boolean) : []);

const formatElapsed = (seconds) => {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return m ? `${m}m ${String(s).padStart(2, "0")}s` : `${s}s`;
};

const formatDateTime = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
};

// A bordered, titled box inside a story card.
const Panel = ({ title, tone = "neutral", wide = false, children }) => (
  <section className={`us-panel us-panel-${tone}${wide ? " us-panel-wide" : ""}`}>
    <header className="us-panel-title">{title}</header>
    <div className="us-panel-body">{children}</div>
  </section>
);

// A panel holding a bullet list; hidden when the list is empty.
const ListPanel = ({ title, items, tone }) => {
  const list = asList(items);
  if (!list.length) return null;
  return (
    <Panel title={title} tone={tone}>
      <ul className="us-list">
        {list.map((item, i) => (
          <li key={i}>{item}</li>
        ))}
      </ul>
    </Panel>
  );
};

// Rules mostly read "Label: value"; show those as aligned rows.
const splitRule = (rule) => {
  const text = String(rule);
  const at = text.indexOf(": ");
  return at > 0 ? [text.slice(0, at), text.slice(at + 2)] : [null, text];
};

const StoryCard = ({ story, expanded, onToggle, writing, written }) => {
  const consulted = asList(story.consulted);
  const informed = asList(story.informed);
  const preconditions = asList(story.preconditions);
  const mainFlow = asList(story.mainFlow);
  const criteria = asList(story.acceptanceCriteria);
  const rules = asList(story.businessRules);
  const roles = [
    { key: "R", label: "Actor", value: story.actor },
    { key: "A", label: "Approver", value: story.approver },
    { key: "C", label: "Consulted", value: consulted.join(", ") },
    { key: "I", label: "Informed", value: informed.join(", ") },
  ].filter((r) => r.value);
  const typeClass = String(story.type || "task").toLowerCase();

  return (
    <article
      className={`role-card us-story us-story-${typeClass}${
        writing ? " us-story-writing" : ""
      }${written ? " us-story-written" : ""}`}
      aria-busy={writing || undefined}
    >
      <button
        type="button"
        className="us-story-header"
        onClick={onToggle}
        aria-expanded={expanded}
      >
        <span className="us-story-id">{story.id}</span>
        <span className="us-story-title">{story.title}</span>
        <span className="us-badges">
          {story.type && (
            <span className={`us-badge us-type-${typeClass}`}>
              {TYPE_LABELS[story.type] || story.type}
            </span>
          )}
          {story.priority && (
            <span
              className={`us-badge us-priority-${story.priority.toLowerCase()}`}
            >
              {story.priority}
            </span>
          )}
          {writing && (
            <span className="us-badge us-writing-badge">AI is writing…</span>
          )}
          {!writing && story.textSource === "AI" && (
            <span className="us-badge us-ai" title="Wording written by AI">
              AI
            </span>
          )}
        </span>
        <span className={`us-caret ${expanded ? "open" : ""}`} aria-hidden="true">
          ▾
        </span>
      </button>

      {story.userStory && (
        <blockquote className="us-quote">{story.userStory}</blockquote>
      )}

      {expanded && (
        <div className="us-story-body">
          {(roles.length > 0 || story.reference) && (
            <div className="us-roles">
              {roles.map((r) => (
                <div key={r.key} className="us-role">
                  <span className={`us-role-key us-role-${r.key.toLowerCase()}`}>
                    {r.key}
                  </span>
                  <span className="us-role-text">
                    <span className="us-role-label">{r.label}</span>
                    <span className="us-role-value">{r.value}</span>
                  </span>
                </div>
              ))}
              {story.reference && (
                <div className="us-reference">{story.reference}</div>
              )}
            </div>
          )}

          <div className="us-grid">
            {(story.trigger || preconditions.length > 0) && (
              <Panel title="Trigger & Preconditions" tone="blue">
                {story.trigger && (
                  <div className="us-trigger">
                    <span className="us-trigger-label">Trigger</span>
                    {story.trigger}
                  </div>
                )}
                {preconditions.length > 0 && (
                  <ul className="us-list">
                    {preconditions.map((item, i) => (
                      <li key={i}>{item}</li>
                    ))}
                  </ul>
                )}
              </Panel>
            )}
            <ListPanel
              title="Postconditions"
              items={story.postconditions}
              tone="green"
            />

            {mainFlow.length > 0 && (
              <Panel title="Main Flow" tone="primary">
                <ol className="us-steps">
                  {mainFlow.map((step, i) => (
                    <li key={i}>
                      <span className="us-step-no">{i + 1}</span>
                      <span>{step}</span>
                    </li>
                  ))}
                </ol>
              </Panel>
            )}
            <ListPanel
              title="Alternate Flows"
              items={story.alternateFlows}
              tone="amber"
            />

            {criteria.length > 0 && (
              <Panel title="Acceptance Criteria" tone="primary" wide>
                <div className="us-table-wrap">
                  <table className="us-table us-ac-table">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Given</th>
                        <th>When</th>
                        <th>Then</th>
                      </tr>
                    </thead>
                    <tbody>
                      {criteria.map((c, i) => (
                        <tr key={i}>
                          <td className="us-ac-no">{i + 1}</td>
                          <td>{c.given}</td>
                          <td>{c.when}</td>
                          <td>{c.then}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Panel>
            )}

            {rules.length > 0 && (
              <Panel title="Business Rules" tone="neutral">
                <dl className="us-rules">
                  {rules.map((rule, i) => {
                    const [label, value] = splitRule(rule);
                    return (
                      <div key={i} className="us-rule">
                        {label && <dt>{label}</dt>}
                        <dd className={label ? "" : "us-rule-full"}>{value}</dd>
                      </div>
                    );
                  })}
                </dl>
              </Panel>
            )}
            <ListPanel title="Assumptions" items={story.assumptions} />
          </div>
        </div>
      )}
    </article>
  );
};

/**
 * User Stories tab of the Process Studio
 * Generates user stories for a process on the backend: an epic, one story per
 * task, decision and sub-process, and the problems found in the process model.
 * The result streams in: every story arrives first in template text, then each
 * TASK story switches to AI text as the AI finishes it, and the final `done`
 * result replaces it all. Generation is rate-limited (5 a minute), so it only
 * runs on a button click.
 */
const ProcessUserStoriesPage = ({ processId: selectedId, onBusyChange }) => {
  const [useAi, setUseAi] = React.useState(true);

  const [result, setResult] = React.useState(null);
  // True once `done` arrives; until then the stories are a live preview.
  const [isFinal, setIsFinal] = React.useState(false);
  const [running, setRunning] = React.useState(false);
  const [status, setStatus] = React.useState(null);
  const [thinking, setThinking] = React.useState("");
  // TASK stories still waiting for AI text, and those it has written.
  const [writingKeys, setWritingKeys] = React.useState(() => new Set());
  const [writtenKeys, setWrittenKeys] = React.useState(() => new Set());
  const [error, setError] = React.useState("");
  const [cancelled, setCancelled] = React.useState(false);
  const [elapsed, setElapsed] = React.useState(0);
  const [collapsed, setCollapsed] = React.useState(() => new Set());
  const abortRef = React.useRef(null);
  const thinkingRef = React.useRef(null);
  // Only the latest request may update the view if the process changes mid-run.
  const requestSeqRef = React.useRef(0);

  React.useEffect(
    () => () => {
      requestSeqRef.current += 1;
      // Leaving the page closes the stream, which stops the AI on the server.
      abortRef.current?.abort();
    },
    [],
  );

  React.useEffect(() => {
    onBusyChange?.(running);
  }, [running, onBusyChange]);

  // AI generation can take minutes, so show how long it has been running.
  React.useEffect(() => {
    if (!running) return undefined;
    setElapsed(0);
    const started = Date.now();
    const id = setInterval(
      () => setElapsed(Math.floor((Date.now() - started) / 1000)),
      1000,
    );
    return () => clearInterval(id);
  }, [running]);

  // Keep the newest AI thinking in view.
  React.useEffect(() => {
    const el = thinkingRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [thinking]);

  const resetRun = React.useCallback(() => {
    setResult(null);
    setIsFinal(false);
    setStatus(null);
    setThinking("");
    setWritingKeys(new Set());
    setWrittenKeys(new Set());
    setError("");
    setCancelled(false);
    setCollapsed(new Set());
  }, []);

  // Stories belong to one process; a new one drops them (and any run in flight).
  React.useEffect(() => {
    requestSeqRef.current += 1;
    abortRef.current?.abort();
    abortRef.current = null;
    setRunning(false);
    resetRun();
  }, [selectedId, resetRun]);

  const handleGenerate = async () => {
    if (!selectedId || running) return;
    const seq = ++requestSeqRef.current;
    const live = () => seq === requestSeqRef.current;
    const controller = new AbortController();
    abortRef.current = controller;
    const ai = useAi;
    let finished = false;

    resetRun();
    setRunning(true);
    setStatus("Starting…");

    try {
      await streamUserStories(selectedId, { ai, signal: controller.signal }, {
        rules: (data) => {
          if (!live()) return;
          setResult(data);
          if (ai) {
            setWritingKeys(
              new Set(
                asList(data?.stories)
                  .filter((s) => s.type === "TASK")
                  .map((s) => s.stepKey),
              ),
            );
            setStatus("Waiting for the AI…");
          }
        },
        status: (data) => {
          if (!live()) return;
          setStatus(PHASE_MESSAGES[data?.phase] || data?.message || null);
        },
        reasoning: (data) => {
          if (!live() || !data?.text) return;
          setThinking((t) => (t + data.text).slice(-MAX_THINKING_CHARS));
        },
        // A preview: the final text comes with `done`. A retry sends the
        // tasks again, so overwrite by stepKey.
        task: (task) => {
          if (!live() || !task?.stepKey) return;
          setResult((r) =>
            r && {
              ...r,
              stories: asList(r.stories).map((s) =>
                s.stepKey === task.stepKey ?
                  {
                    ...s,
                    userStory: task.userStory || s.userStory,
                    mainFlow:
                      asList(task.mainFlow).length >= 2 ?
                        task.mainFlow
                      : s.mainFlow,
                    textSource: "AI",
                  }
                : s,
              ),
            },
          );
          setWritingKeys((k) => {
            const next = new Set(k);
            next.delete(task.stepKey);
            return next;
          });
          setWrittenKeys((k) => new Set(k).add(task.stepKey));
        },
        done: (data) => {
          finished = true;
          if (!live()) return;
          setResult(data);
          setIsFinal(true);
        },
        error: (data) => {
          finished = true;
          if (!live()) return;
          // Keep the stories already on screen.
          setError(data?.message || "Something went wrong. Please try again.");
        },
      });
      if (!finished && live()) {
        setError(
          "The connection closed before the user stories were finished. Please try again.",
        );
      }
    } catch (e) {
      if (!live()) return;
      if (e?.name === "AbortError") {
        setCancelled(true);
      } else {
        console.error("Failed to generate user stories:", e);
        setError(userStoriesErrorMessage(e));
      }
    } finally {
      if (live()) {
        setRunning(false);
        setStatus(null);
        setWritingKeys(new Set());
        abortRef.current = null;
      }
    }
  };

  const handleCancel = () => abortRef.current?.abort();

  const stories = asList(result?.stories);
  const issues = asList(result?.modelIssues);
  const generation = result?.generation || {};
  const epic = result?.epic || {};
  const totalTasks = stories.filter((s) => s.type === "TASK").length;
  const aiUnavailable =
    isFinal && generation.aiRequested && !generation.aiUsed;
  const streamingAi = running && result && useAi;

  const toggleStory = (key) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  const allCollapsed = stories.length > 0 && collapsed.size === stories.length;
  const toggleAll = () =>
    setCollapsed(
      allCollapsed ? new Set() : new Set(stories.map((s, i) => s.stepKey || i)),
    );

  return (
    <div className="ps-tab-body">
      <div className="role-card">
        <div className="section-title">Generate User Stories</div>
        <div className="form-grid">
          <div className="form-group" style={{ alignSelf: "end" }}>
            <label
              className="us-ai-toggle"
              title="AI wording takes a few minutes; the stories appear in template text right away and switch to AI text as it is written. Without AI, rule-based wording is returned in a few seconds."
            >
              <input
                type="checkbox"
                checked={useAi}
                onChange={(e) => setUseAi(e.target.checked)}
                disabled={running}
              />
              Write stories with AI
            </label>
          </div>
          <div className="form-group us-actions" style={{ alignSelf: "end" }}>
            <button
              className="primary-btn"
              onClick={handleGenerate}
              disabled={!selectedId || running}
            >
              {running ? "Generating…"
              : result ? "Regenerate User Stories"
              : "Generate User Stories"}
            </button>
            {running && (
              <button
                type="button"
                className="secondary-btn"
                onClick={handleCancel}
                title="Stop generating; this also stops the AI on the server"
              >
                Cancel
              </button>
            )}
          </div>
        </div>
      </div>

      {error && (
        <div role="alert" className="us-alert us-alert-error">
          ⚠ {error}
        </div>
      )}

      {cancelled && (
        <div role="status" className="us-alert us-alert-info">
          Generation cancelled.
          {result && " The stories below were not finished by the AI."}
        </div>
      )}

      {running && (
        <div className="role-card us-progress" role="status">
          <div className="us-progress-line">
            <span className="us-spinner" aria-hidden="true" />
            <span className="us-progress-text">
              {status || "Generating user stories…"}
            </span>
            <span className="us-muted">{formatElapsed(elapsed)}</span>
          </div>
          {streamingAi && totalTasks > 0 && (
            <>
              <div
                className="us-progress-bar"
                role="progressbar"
                aria-valuemin={0}
                aria-valuemax={totalTasks}
                aria-valuenow={writtenKeys.size}
              >
                <span
                  style={{
                    width: `${Math.min(100, (writtenKeys.size / totalTasks) * 100)}%`,
                  }}
                />
              </div>
              <div className="us-muted">
                AI wrote {writtenKeys.size} of {totalTasks} task stories. The
                stories below are a preview until the AI finishes.
              </div>
            </>
          )}
          {!result && (
            <span className="us-muted">
              {useAi ?
                "The stories will appear in a moment and switch to AI text as it is written."
              : "This usually takes a few seconds."}
            </span>
          )}
          {thinking && (
            <details className="us-thinking">
              <summary>AI is thinking…</summary>
              <pre ref={thinkingRef}>{thinking}</pre>
            </details>
          )}
        </div>
      )}

      {result && (
        <>
          {aiUnavailable ?
            <div role="alert" className="us-alert us-alert-warning">
              ⚠ AI unavailable, showing template text
              {generation.aiError ? `: ${generation.aiError}` : "."}
            </div>
          : isFinal &&
            generation.aiError && (
              <div role="alert" className="us-alert us-alert-warning">
                ⚠ AI text was not used: {generation.aiError}
              </div>
            )
          }

          <div className="role-card us-epic">
            <div className="us-epic-head">
              {epic.id && <span className="us-story-id">{epic.id}</span>}
              <span className="section-title" style={{ margin: 0 }}>
                {epic.title || result.process?.name || "Epic"}
              </span>
            </div>
            {epic.statement && (
              <blockquote className="us-quote">{epic.statement}</blockquote>
            )}
            {epic.summary && <p className="us-summary">{epic.summary}</p>}
            <div className="us-meta">
              {result.process?.code && <span>Process {result.process.code}</span>}
              {result.process?.version != null && (
                <span>Version {result.process.version}</span>
              )}
              {result.flowSource && <span>Flow from {result.flowSource}</span>}
              {result.generatedAt && (
                <span>Generated {formatDateTime(result.generatedAt)}</span>
              )}
              {isFinal && (
                <span>
                  {generation.aiUsed ?
                    `AI: ${generation.model || generation.provider || "used"}`
                  : "Template wording"}
                </span>
              )}
            </div>
          </div>

          {issues.length > 0 && (
            <div className="role-card" style={{ marginTop: 12 }}>
              <div className="section-title">
                Model Issues ({issues.length})
              </div>
              <div className="us-table-wrap">
                <table className="us-table">
                  <thead>
                    <tr>
                      <th>Severity</th>
                      <th>Issue</th>
                      <th>Location</th>
                      <th>Effect</th>
                      <th>Recommended Fix</th>
                      <th>Source</th>
                    </tr>
                  </thead>
                  <tbody>
                    {issues.map((issue, i) => (
                      <tr key={i}>
                        <td>
                          <span
                            className={`us-badge us-severity-${String(
                              issue.severity || "low",
                            ).toLowerCase()}`}
                          >
                            {issue.severity}
                          </span>
                        </td>
                        <td>{issue.issue}</td>
                        <td>{issue.location}</td>
                        <td>{issue.effect}</td>
                        <td>{issue.recommendedFix}</td>
                        <td>{issue.source}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <div className="us-stories-head">
            <div className="section-title" style={{ margin: 0 }}>
              User Stories ({stories.length})
            </div>
            {stories.length > 0 && (
              <button type="button" className="secondary-btn" onClick={toggleAll}>
                {allCollapsed ? "Expand all" : "Collapse all"}
              </button>
            )}
          </div>

          {stories.length === 0 ?
            <div className="role-card us-muted">No user stories were returned.</div>
          : stories.map((story, i) => {
              const key = story.stepKey || i;
              return (
                <StoryCard
                  key={key}
                  story={story}
                  expanded={!collapsed.has(key)}
                  onToggle={() => toggleStory(key)}
                  writing={writingKeys.has(story.stepKey)}
                  written={writtenKeys.has(story.stepKey)}
                />
              );
            })
          }
        </>
      )}
    </div>
  );
};

export default ProcessUserStoriesPage;
